import { useState } from 'react';
import { usePatientCard } from '../../../../hooks/usePatientCard';
import type { DmMessagePatientDetail } from '../../../../services/dmMessagesInbox';
import { DmControlRoute } from '../../../patient-files/actions/dm/control/DmControlRoute';
import { ScanComplianceStrip } from '../../../patient-files/overview/ScanComplianceStrip';
import { TaskSidebarPanel } from '../../../work-queue/tasks/TaskSidebarPanel';
import type { SidebarTabKey } from '../../../work-queue/tasks/taskSidebarTabs';
import { useInitialSidebarTab } from '../../shared/caseActionFlow';
import { PatientViewHeader } from '../../shared/PatientViewHeader';
import {
    PatientViewError,
    PatientViewNotFound,
    PatientViewSkeleton
} from '../../shared/PatientViewStates';
import { ToReviewPill } from '../ToReviewPill';
import { TriageHeaderControls } from '../triage/TriageHeaderControls';
import { toAiReplySource } from '../triage/aiTriageState';
import { MessagesThreadCard } from './MessagesThreadCard';
import { useMessagePatientScreen } from './useMessagePatientScreen';

const DEFAULT_TAB: SidebarTabKey = 'dm-notifications';
const VAS_TAB: SidebarTabKey = 'vas';
const BACK_LABEL = 'Back to Messages';

interface MessageThreadColumnProps {
    patientId: string;
    patient: DmMessagePatientDetail;
}

function MessageThreadColumn({ patientId, patient }: MessageThreadColumnProps) {
    const aiReply = toAiReplySource(patient);
    const dmId = usePatientCard(patientId).data?.monitoring?.dmId ?? null;
    return (
        <div className="flex min-h-0 min-w-0 flex-[1] flex-col gap-3">
            <ScanComplianceStrip patientId={patientId} dmId={dmId} />
            <div className="min-h-0 flex-1">
                <MessagesThreadCard
                    patientId={patientId}
                    unreviewedCount={patient.unreviewedCount}
                    lastResolution={patient.lastResolution}
                    aiReply={aiReply}
                />
            </div>
        </div>
    );
}

// The DM thread on the left, with the patient's notifications and tools on the right.
export function MessagePatientView({ patientId }: { patientId: string }) {
    const { view, listSummary, goPrevious, goNext, backToList, tabs } =
        useMessagePatientScreen(patientId);
    const initialTab = useInitialSidebarTab(DEFAULT_TAB);
    const [activeTab, setActiveTab] = useState<string>(initialTab);

    if (view.status === 'loading') return <PatientViewSkeleton />;
    if (view.status === 'notFound') {
        return <PatientViewNotFound onBackToList={backToList} backLabel={BACK_LABEL} />;
    }
    if (view.status === 'error' || !view.patient) {
        return (
            <PatientViewError
                subject="this patient's messages"
                onRetry={view.retry}
                onBackToList={backToList}
                backLabel={BACK_LABEL}
            />
        );
    }

    return (
        <div className="flex h-full min-h-0 flex-col gap-3 px-8 pb-4">
            <PatientViewHeader
                patient={view.patient}
                badges={<ToReviewPill count={view.patient.unreviewedCount} />}
                controls={
                    <TriageHeaderControls patientId={patientId} triage={view.patient.triage} />
                }
                backLabel={BACK_LABEL}
                onBack={backToList}
                onPrevious={goPrevious}
                onNext={goNext}
                position={view.positionLabel}
                listSummary={listSummary}
                onShowVirtualAppointments={() => setActiveTab(VAS_TAB)}
            />
            <div className="flex min-h-0 flex-1 gap-3">
                <MessageThreadColumn patientId={patientId} patient={view.patient} />
                <div className="min-h-0 min-w-0 flex-[2]">
                    <TaskSidebarPanel
                        tabs={tabs}
                        activeKey={activeTab}
                        onActiveKeyChange={setActiveTab}
                        keepMounted
                    />
                </div>
            </div>
            <DmControlRoute patientId={patientId} patientName={view.patient.patientName} />
        </div>
    );
}
